import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ListTableShell } from "@/components/ResourceShell";
import { ListToolbar } from "@/components/ListToolbar";
import { CustomerStatusBadge } from "@/components/StatusBadges";
import { CreateCustomerDialog } from "@/components/dialogs/CreateCustomerDialog";
import { useCustomers } from "@/api/queries/customers";
import { relativeTime } from "@/lib/relativeTime";

export function CustomersList() {
  const customers = useCustomers();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const all = customers.data ?? [];
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return all;
    return all.filter((c) => c.name.toLowerCase().includes(term) || c.email.toLowerCase().includes(term) || (c.code ?? "").includes(term) || (c.code ?? "").includes(term));
  }, [all, q]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-text3">
          {filtered.length} von {all.length} Kunden
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="create-customer-button">
          <Plus className="h-4 w-4" /> Kunde anlegen
        </Button>
      </div>

      <ListToolbar search={q} onSearch={setQ} placeholder="Kunde suchen…" />

      <ListTableShell
        isLoading={customers.isLoading}
        isError={customers.isError}
        errorMessage={customers.error?.message}
        isEmpty={filtered.length === 0}
        emptyMessage={all.length === 0 ? "Noch keine Kunden" : "Keine Treffer"}
        onRetry={() => customers.refetch()}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Kennung</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Angelegt</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((c) => (
              <TableRow key={c.id} data-clickable="true" onClick={() => navigate(`/customers/${c.id}`)}>
                <TableCell className="mono text-xs" data-testid={`customer-code-${c.id}`}>{c.code}</TableCell>
                <TableCell className="mono text-xs" data-testid={`customer-code-${c.id}`}>{c.code}</TableCell>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="text-text2">{c.email}</TableCell>
                <TableCell>
                  <CustomerStatusBadge status={c.status} />
                </TableCell>
                <TableCell className="text-text3">{relativeTime(c.createdAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListTableShell>

      {createOpen && <CreateCustomerDialog open={createOpen} onOpenChange={setCreateOpen} />}
    </div>
  );
}
